/**
 * The hand, and the dirt.
 *
 * Everything a person leaves on a surface, and everything time leaves on it
 * afterwards, drawn on a canvas: the pen, the scratch, the line somebody put
 * through what they had written, the grease of a palm on plastic.
 *
 * This is the vocabulary; `propdecals.ts` decides what the marks *say* and
 * `propslide.ts` decides what the dirt sits on. Split out because the two had
 * started to share primitives and because the interesting part of a horror
 * detail is almost never the shape — it is that the shape was made by somebody
 * doing something, and that only survives if the hand is modelled once and
 * reused.
 *
 * Nothing in here depends on anything else in `render/`, which is what keeps
 * `propdecals.ts` free to import it.
 */

import * as THREE from 'three';
import { clamp01, TAU } from '../core/math';
import { makeRNG } from '../core/rng';
import type { RNG } from '../core/types';

/** `#rrggbb` plus an alpha, as Canvas2D wants it. */
export function rgba(hex: string, alpha: number): string {
  const n = parseInt(hex.replace('#', ''), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${clamp01(alpha).toFixed(3)})`;
}

/* ------------------------------------------------------------------ *
 * The pen
 * ------------------------------------------------------------------ */

/**
 * Write text one glyph at a time with the pen wandering: each letter leans a
 * little, sits a little off the line, and gets a second dry pass that misses.
 * Letters that came out wet drip.
 *
 * `fade` scales every alpha, which is how one hand comes out as somebody
 * writing now and another as somebody who wrote a long time ago. It multiplies
 * rather than being applied as a `globalAlpha` by the caller because the
 * per-glyph alpha is the whole texture of the thing — flattening it would give
 * back an evenly grey line of type.
 *
 * Returns the x it finished at.
 */
export function scrawl(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  baseline: number,
  size: number,
  ink: string,
  rng: RNG,
  fade = 1
): number {
  ctx.font = `bold ${size}px sans-serif`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  ctx.strokeStyle = ink;
  ctx.fillStyle = ink;

  // The whole line sags a touch, the way a hand writing at shoulder height does.
  const sag = rng.range(0.008, 0.03) * (rng.chance(0.5) ? 1 : -1);
  let cursor = x;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    const w = ctx.measureText(ch).width;
    if (ch === ' ') {
      cursor += w * rng.range(0.8, 1.05);
      continue;
    }

    // The marker gives out and is pressed harder to bring it back.
    const wet = rng.range(0.45, 1);
    const y = baseline + (cursor - x) * sag + rng.range(-size * 0.05, size * 0.05);

    ctx.save();
    ctx.translate(cursor, y);
    ctx.rotate(rng.range(-0.09, 0.09));
    ctx.globalAlpha = (0.5 + wet * 0.5) * fade;
    ctx.lineWidth = size * 0.07;
    ctx.strokeText(ch, 0, 0);
    ctx.fillText(ch, 0, 0);
    // A second pass, offset: the stroke doubled back and did not line up.
    ctx.globalAlpha = (0.16 + wet * 0.2) * fade;
    ctx.fillText(ch, rng.range(-1.6, 1.6), rng.range(-1.4, 1.4));
    ctx.restore();

    if (wet > 0.8 && rng.chance(0.3)) {
      const run = rng.range(size * 0.2, size * 0.6);
      const dx = cursor + w * rng.range(0.2, 0.8);
      const grad = ctx.createLinearGradient(dx, y, dx, y + run);
      grad.addColorStop(0, rgba(ink, 0.5 * fade));
      grad.addColorStop(1, rgba(ink, 0));
      ctx.save();
      ctx.strokeStyle = grad;
      ctx.lineWidth = size * 0.035;
      ctx.beginPath();
      ctx.moveTo(dx, y - size * 0.05);
      ctx.lineTo(dx + rng.range(-1.5, 1.5), y + run);
      ctx.stroke();
      ctx.restore();
    }

    cursor += w * rng.range(0.9, 1.05);
  }
  return cursor;
}

/**
 * A line gouged into wallpaper: a pale lip where the paper lifted, and the
 * darker groove beside it. Two strokes is the whole trick, and it is the
 * difference between "scratched" and "drawn".
 */
export function scratch(
  ctx: CanvasRenderingContext2D,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  width: number,
  ink: string,
  lip: string,
  rng: RNG
): void {
  const mx = (ax + bx) / 2 + rng.range(-width, width);
  const my = (ay + by) / 2 + rng.range(-width, width);

  ctx.save();
  ctx.lineCap = 'round';
  ctx.globalAlpha = 0.35;
  ctx.strokeStyle = lip;
  ctx.lineWidth = width * 1.2;
  ctx.beginPath();
  ctx.moveTo(ax + 1, ay - 1);
  ctx.quadraticCurveTo(mx + 1, my - 1, bx + 1, by - 1);
  ctx.stroke();

  ctx.globalAlpha = rng.range(0.72, 1);
  ctx.strokeStyle = ink;
  ctx.lineWidth = width;
  ctx.beginPath();
  ctx.moveTo(ax, ay);
  ctx.quadraticCurveTo(mx, my, bx, by);
  ctx.stroke();
  ctx.restore();
}

/* ------------------------------------------------------------------ *
 * What happened to it afterwards
 * ------------------------------------------------------------------ */

/**
 * A line dragged through writing that is already there.
 *
 * Two passes, because a person crossing something out almost never does it once
 * — and the second pass is always harder and longer than the first, which is
 * the part that reads as somebody having minded.
 */
export function strikeOut(
  ctx: CanvasRenderingContext2D,
  x0: number,
  x1: number,
  y: number,
  ink: string,
  rng: RNG
): void {
  ctx.save();
  ctx.lineCap = 'round';
  ctx.strokeStyle = ink;
  for (let pass = 0; pass < 2; pass++) {
    const over = pass === 0 ? 4 : rng.range(10, 22);
    ctx.globalAlpha = pass === 0 ? 0.72 : 0.9;
    ctx.lineWidth = pass === 0 ? 3 : rng.range(4, 6);
    ctx.beginPath();
    ctx.moveTo(x0 - over, y + rng.range(-5, 5));
    ctx.quadraticCurveTo((x0 + x1) / 2, y + rng.range(-9, 9), x1 + over, y + rng.range(-5, 5));
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * The pen went down the wall.
 *
 * Whatever was being written stopped here, and the hand kept going. A word that
 * simply ends could be a word that ran out of room; a word that ends in a
 * thirty-centimetre drag could not.
 */
export function dragOff(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  ink: string,
  rng: RNG
): void {
  const run = size * rng.range(1.4, 2.6);
  const grad = ctx.createLinearGradient(x, y - size * 0.4, x + rng.range(-6, 10), y + run);
  grad.addColorStop(0, rgba(ink, 0.85));
  grad.addColorStop(0.55, rgba(ink, 0.4));
  grad.addColorStop(1, rgba(ink, 0));
  ctx.save();
  ctx.lineCap = 'round';
  ctx.strokeStyle = grad;
  ctx.lineWidth = size * 0.09;
  ctx.beginPath();
  ctx.moveTo(x, y - size * 0.5);
  ctx.quadraticCurveTo(x + rng.range(-4, 8), y + run * 0.5, x + rng.range(-10, 14), y + run);
  ctx.stroke();
  ctx.restore();
}

/* ------------------------------------------------------------------ *
 * Hands
 * ------------------------------------------------------------------ */

/**
 * The grease of a palm and four fingers, smudged in the direction it slipped.
 *
 * Not a print — a print is a shape people read as evidence, which is a
 * different and much louder thing. This is the mark a hand leaves on a surface
 * it took hold of: a soft patch and four short streaks above it, drawn with
 * enough blur that at two metres it is only dirt, and at half a metre it is
 * not.
 */
export function palmSmudge(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  angle: number,
  tone: string,
  alpha: number,
  rng: RNG
): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.globalAlpha = alpha;

  const heel = ctx.createRadialGradient(0, size * 0.3, size * 0.05, 0, size * 0.3, size * 0.5);
  heel.addColorStop(0, rgba(tone, 0.85));
  heel.addColorStop(1, rgba(tone, 0));
  ctx.fillStyle = heel;
  ctx.beginPath();
  ctx.ellipse(0, size * 0.3, size * 0.42, size * 0.34, 0, 0, TAU);
  ctx.fill();

  ctx.lineCap = 'round';
  for (let f = 0; f < 4; f++) {
    const fx = (f - 1.5) * size * 0.22;
    const len = size * rng.range(0.34, 0.52);
    const streak = ctx.createLinearGradient(fx, 0, fx + rng.range(-2, 2), -len);
    streak.addColorStop(0, rgba(tone, 0.7));
    streak.addColorStop(1, rgba(tone, 0));
    ctx.strokeStyle = streak;
    ctx.lineWidth = size * rng.range(0.1, 0.14);
    ctx.beginPath();
    ctx.moveTo(fx, size * 0.05);
    ctx.lineTo(fx + rng.range(-3, 3), -len);
    ctx.stroke();
  }
  ctx.restore();
}

/* ------------------------------------------------------------------ *
 * Dirt
 * ------------------------------------------------------------------ */

/**
 * The grime map for a length of moulded plastic that people have been getting
 * into.
 *
 * Multiplied against the material's own colour, so it is drawn near white and
 * darkened rather than tinted: the same canvas serves a red tube and a blue
 * one. Laid out for a cylinder's UVs — `u` runs around the circumference and
 * `v` along the length, with `v = 0` at the end the caller passed first.
 *
 *  - **v = 0 is the mouth.** It is scuffed and handled and pale.
 *  - **v = 1 is the far end.** It goes darker the whole way, so the tube reads
 *    as receding even before the throat inside it does.
 *  - **The streaks run along v**, because whatever has gone down this has gone
 *    down it lengthways, and there have been enough of them to polish a track.
 */
export function makePlasticGrime(seed: number, hands: number): THREE.Texture {
  const W = 512;
  const H = 256;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;
  const rng = makeRNG(seed);

  // Clean plastic, slightly off white so the multiply never brightens.
  ctx.fillStyle = '#efeeea';
  ctx.fillRect(0, 0, W, H);

  // Mottling. Moulded plastic is never even, and a flat multiply map is the
  // fastest way to make an object look like a render of an object.
  ctx.globalAlpha = 0.05;
  ctx.fillStyle = '#6d6558';
  for (let i = 0; i < 140; i++) {
    ctx.beginPath();
    ctx.ellipse(rng.range(0, W), rng.range(0, H), rng.range(6, 42), rng.range(4, 26), rng.range(0, TAU), 0, TAU);
    ctx.fill();
  }

  // Into the dark. Vertical here is *along* the tube.
  const depth = ctx.createLinearGradient(0, 0, 0, H);
  depth.addColorStop(0, 'rgba(60,54,44,0.02)');
  depth.addColorStop(0.55, 'rgba(60,54,44,0.2)');
  depth.addColorStop(1, 'rgba(38,34,28,0.55)');
  ctx.globalAlpha = 1;
  ctx.fillStyle = depth;
  ctx.fillRect(0, 0, W, H);

  // The track. Long runs of dirt down the length, a few of them polished pale
  // in the middle where the traffic has been.
  ctx.lineCap = 'round';
  for (let i = 0; i < 26; i++) {
    const x = rng.range(0, W);
    const pale = rng.chance(0.3);
    ctx.globalAlpha = pale ? rng.range(0.1, 0.22) : rng.range(0.12, 0.3);
    ctx.strokeStyle = pale ? '#ffffff' : '#4a4238';
    ctx.lineWidth = rng.range(2, pale ? 26 : 12);
    ctx.beginPath();
    ctx.moveTo(x, rng.range(-20, H * 0.35));
    ctx.quadraticCurveTo(x + rng.range(-14, 14), H * 0.5, x + rng.range(-22, 22), rng.range(H * 0.7, H + 20));
    ctx.stroke();
  }

  // Where the hands went. Clustered at the mouth, all of them dragged the same
  // way — toward the far end, because everybody who left one was getting in.
  for (let i = 0; i < hands; i++) {
    palmSmudge(
      ctx,
      rng.range(0, W),
      rng.range(H * 0.04, H * 0.26),
      rng.range(26, 40),
      Math.PI + rng.range(-0.5, 0.5),
      '#4b4136',
      rng.range(0.22, 0.5),
      rng
    );
  }

  // Scuffing right at the lip, where feet and shoulders have gone over the edge.
  ctx.globalAlpha = 1;
  const lip = ctx.createLinearGradient(0, 0, 0, H * 0.1);
  lip.addColorStop(0, 'rgba(255,255,255,0.28)');
  lip.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = lip;
  ctx.fillRect(0, 0, W, H * 0.1);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  // The seam runs the length of the tube and lands wherever the quaternion in
  // `strut` put it; wrapping in u keeps it from being a hard edge.
  texture.wrapS = THREE.RepeatWrapping;
  return texture;
}
